import React from 'react';
import { Table, Badge, Spinner, Stack } from 'react-bootstrap';
import { useSmsHistoryQuery } from '../../queries/notification.query';
import { SmsLog } from '../../Types';

interface ClientSmsHistoryProps {
  clientId: string;
}

const getSmsTypeLabel = (smsType: string): string => {
  const typeMap: Record<string, string> = {
    ClientClassificationReminder: 'Classification Reminder',
    PaymentReminder: 'Payment Reminder',
    DelinquentNotice: 'Delinquent Notice',
    TicketStatusUpdate: 'Status Update',
    GeneralNotification: 'General',
    BusinessPlateNotice: 'Corporate Plate Notice',
    RegistrationRequest: 'Registration Request'
  };
  return typeMap[smsType] || smsType;
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'Success':
      return <Badge bg="success">Success</Badge>;
    case 'Failed':
      return <Badge bg="danger">Failed</Badge>;
    case 'Pending':
      return <Badge bg="warning">Pending</Badge>;
    default:
      return <Badge bg="secondary">{status}</Badge>;
  }
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const ClientSmsHistory: React.FC<ClientSmsHistoryProps> = ({ clientId }) => {
  const { data, isLoading, isError } = useSmsHistoryQuery(clientId);

  if (isLoading) {
    return (
      <Stack className="mt-4 mb-4">
        <h2>SMS History</h2>
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack className="mt-4 mb-4">
        <h2>SMS History</h2>
        <p className="text-danger">Failed to load SMS history</p>
      </Stack>
    );
  }

  const smsLogs: SmsLog[] = data?.data || [];

  return (
    <Stack className="mt-4 mb-4">
      <h2>SMS History</h2>
      {smsLogs.length === 0 ? (
        <p className="text-muted">No SMS messages have been sent to this client.</p>
      ) : (
        <Table responsive striped bordered hover size="sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Phone</th>
              <th>Type</th>
              <th>Reminder #</th>
              <th>Status</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {smsLogs.map((log) => (
              <tr key={log.id}>
                <td>{formatDate(log.sentAt)}</td>
                <td>{log.phoneNumber}</td>
                <td>{getSmsTypeLabel(log.smsType)}</td>
                <td>{log.sendCount}</td>
                <td>{getStatusBadge(log.status)}</td>
                <td>
                  {log.errorMessage ? (
                    <span className="text-danger" title={log.errorMessage}>
                      {log.errorMessage.length > 50
                        ? `${log.errorMessage.substring(0, 50)}...`
                        : log.errorMessage}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Stack>
  );
};
